// --- FULL IMPACT DATA WITH SECTORS AND DATA REQUIREMENTS ---
export const impactsData = [
  {
    id: 'infra-urban',
    title: 'Infrastructure and Urban Services',
    desc: 'Impact on urban services, infrastructure, and public systems',
    status: 'Warning',
    color: 'warning',
    sectors: [
      {
        name: 'Power',
        indicators: [
          {
            name: 'Power outages (duration and geographic scope)',
            threshold: 'Outage duration > 2 hours',
            dataNeeded: 'Number of households without electricity & duration of outage during a heatwave',
            recommendedMeasures: [
              'Load balancing & staggered supply scheduling',
              'Deploy emergency diesel generators at critical locations',
              'SMS alerts to public'
            ]
          }
        ]
      },
      {
        name: 'Transport',
        indicators: [
          {
            name: 'Public transport disruptions (rail, metro, bus)',
            threshold: 'Delays > 30 mins',
            dataNeeded: '% of delay/cancellation in services due to infrastructure overheating (e.g., rails, signals, AC failure)',
            recommendedMeasures: [
              'Reroute services',
              'Use of emergency bus fleets',
              'Prioritize cooling system maintenance on heat days'
            ]
          },
          {
            name: 'Road/bridge degradation or closures',
            threshold: '% Road affected > 5%',
            dataNeeded: '% of major road segments degraded (buckling, softening), or closed due to heat damage',
            recommendedMeasures: [
              'Rapid-response road repair teams',
              'Ban on heavy vehicle movement during peak heat hours'
            ]
          },
          {
            name: 'Traffic signal / equipment failures',
            threshold: 'Malfunction on red-alert days',
            dataNeeded: 'Number of non-functioning traffic signals or heat-triggered malfunctions in transport systems',
            recommendedMeasures: [
              'Increase inspection frequency',
              'Backup power for key junctions',
              'Public communication via radio/traffic signage'
            ]
          }
        ]
      },
      {
        name: 'Buildings (Public)',
        indicators: [
          {
            name: 'Cooling system failure in public buildings',
            threshold: 'HVAC breakdown > 10%',
            dataNeeded: 'Number of government hospitals, shelters, schools with malfunctioning ACs or coolers',
            recommendedMeasures: [
              'Emergency repair crews',
              'Provision of mobile cooling units (portable air coolers)',
              'Prioritization of relief infrastructure cooling coverage'
            ]
          }
        ]
      },
      {
        name: 'Water',
        indicators: [
          {
            name: 'Drop in per capita water availability (lpcd)',
            threshold: 'Per capita availability < 135 lpcd',
            dataNeeded: 'Daily litres per capita (ward-level, colony-level) vs. seasonal average',
            recommendedMeasures: [
              'Increase tanker supply in affected zones',
              'Prioritize hospitals and slum clusters',
              'Promote community rainwater harvesting',
              'Repair leakages rapidly'
            ]
          },
          {
            name: 'Percentage of wards dependent on water tankers',
            threshold: '% Wards relying on tankers > 40%',
            dataNeeded: 'Proportion of urban wards where more than "X%" households rely on tankers for > "N" days',
            recommendedMeasures: [
              'Real-time water dashboard',
              'Special allocation of diesel & tankers',
              'Collaboration with disaster volunteers'
            ]
          },
          {
            name: 'Groundwater stress levels',
            threshold: 'Water table drop > 2m/year',
            dataNeeded: 'Groundwater table depth trends (pre-monsoon and summer weeks)',
            recommendedMeasures: [
              'Deep aquifer tracking',
              'Emergency borewell revival',
              'Artificial recharge actions'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'economy',
    title: 'Economy and Productivity',
    desc: 'Impact on workforce productivity, labor, and education sector',
    status: 'Warning',
    color: 'warning',
    sectors: [
      {
        name: 'Education',
        indicators: [
          {
            name: 'No. of school/college closure days due to heat',
            threshold: '> 5 days/season',
            dataNeeded: 'Number of closure days officially declared by Delhi Govt or school authorities',
            recommendedMeasures: [
              'Implement flexible school hours during extreme heat',
              'Provide cooling infrastructure in classrooms',
              'Develop heat action plans for educational institutions',
              'Coordinate with health department for student safety'
            ]
          }
        ]
      },
      {
        name: 'Buildings (Educational)',
        indicators: [
          {
            name: '% of schools without basic cooling infrastructure',
            threshold: '> 80% classrooms w/o fans',
            dataNeeded: 'No. of government schools without fans, coolers, or shaded playgrounds',
            recommendedMeasures: [
              'Install ceiling fans and coolers in all classrooms',
              'Create shaded areas in playgrounds',
              'Provide portable cooling units for emergency use',
              'Prioritize schools in high-heat zones'
            ]
          }
        ]
      },
      {
        name: 'Construction',
        indicators: [
          {
            name: 'Lost labor hours due to heat stress',
            threshold: 'Productivity reduction > 15%',
            dataNeeded: '% reduction in productive hours in outdoor labor sectors (construction, sanitation, delivery, etc.)',
            recommendedMeasures: [
              'Implement mandatory rest breaks during peak heat hours',
              'Shift work schedules to cooler times of day',
              'Provide shaded rest areas at worksites',
              'Enforce heat stress prevention protocols'
            ]
          },
          {
            name: '% of workers without access to shade/water',
            threshold: 'Sites with 0 access',
            dataNeeded: '% of laborers at informal or construction worksites lacking basic protection from heat',
            recommendedMeasures: [
              'Mandate provision of shade structures at all worksites',
              'Ensure continuous access to clean drinking water',
              'Conduct regular site inspections',
              'Provide cooling stations and first aid'
            ]
          }
        ]
      },
      {
        name: 'Manufacturing and industrial',
        indicators: [
          {
            name: 'Reported workplace heat illness cases',
            threshold: 'Reported cases > 5/day',
            dataNeeded: 'Weekly number of cases of heatstroke, exhaustion, and dehydration reported at major worksites',
            recommendedMeasures: [
              'Immediate medical response protocols',
              'Worker training on heat illness recognition',
              'Mandatory hydration breaks',
              'Emergency cooling facilities at worksites'
            ]
          }
        ]
      },
      {
        name: 'Daily Wage Labor',
        indicators: [
          {
            name: 'No show rate among informal workers',
            threshold: 'Absenteeism > 20%',
            dataNeeded: '% of daily-wage workers not reporting to work due to heat',
            recommendedMeasures: [
              'Provide alternative work arrangements during extreme heat',
              'Establish cooling centers near work sites',
              'Financial support for affected workers',
              'Flexible work hours and locations'
            ]
          }
        ]
      },
      {
        name: 'Urban Informal services',
        indicators: [
          {
            name: 'Daily wage loss (street vendors, rickshaw pullers)',
            threshold: 'Daily wage loss > ₹200',
            dataNeeded: 'Average daily income loss in ₹ for urban informal workers',
            recommendedMeasures: [
              'Provide shaded vending zones',
              'Establish temporary market shelters',
              'Financial assistance programs',
              'Promote evening/night markets during heatwaves'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'social',
    title: 'Social Vulnerability',
    desc: 'Impact on vulnerable populations and at-risk communities',
    status: 'Critical',
    color: 'critical',
    sectors: [
      {
        name: 'Built Environment & Public Health',
        indicators: [
          {
            name: 'Low-income housing without cooling access',
            threshold: '% Low-income households w/o fans > 40%',
            dataNeeded: 'HH-level cooling asset data (fan/cooler/AC) from surveys',
            recommendedMeasures: [
              'Subsidized cooling equipment distribution',
              'Community cooling centers in low-income areas',
              'Roof insulation and reflective paint programs',
              'Emergency cooling assistance hotline'
            ]
          }
        ]
      },
      {
        name: 'Social Welfare & Humanitarian Response',
        indicators: [
          {
            name: 'Homeless and street dwellers exposed',
            threshold: '% Unsheltered homeless in hotspots > 15%',
            dataNeeded: 'Number of street dwellers without cooling or shaded night shelter',
            recommendedMeasures: [
              'Activate emergency shelters with cooling facilities',
              'Mobile cooling units in high-density areas',
              'Outreach programs to identify vulnerable individuals',
              '24/7 cooling centers during heat alerts'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'ecosystem',
    title: 'Ecosystem and Environment',
    desc: 'Urban heat island effects and environmental impacts',
    status: 'Warning',
    color: 'warning',
    sectors: [
      {
        name: 'Urban Planning & Meteorology',
        indicators: [
          {
            name: 'Temperature differential (urban vs. rural)',
            threshold: 'Difference > 4°C sustained',
            dataNeeded: 'Hourly and daily temperature readings across urban wards and outskirts (e.g., Najafgarh vs. Connaught Place)',
            recommendedMeasures: [
              'Increase green cover in urban heat islands',
              'Promote reflective surfaces and cool roofs',
              'Reduce vehicular emissions and heat-generating activities',
              'Urban planning to improve air circulation'
            ]
          }
        ]
      },
      {
        name: 'Green Infrastructure & Geospatial Analysis',
        indicators: [
          {
            name: 'Surface temperature & low vegetation zones',
            threshold: 'LST > 45°C or NDVI < 0.2',
            dataNeeded: 'Satellite-based LST (Land Surface Temperature), NDVI (Normalized Difference Vegetation Index)',
            recommendedMeasures: [
              'Afforestation programs in identified hotspots',
              'Green roof and wall installations',
              'Convert impervious surfaces to permeable materials',
              'Create urban parks and green corridors'
            ]
          }
        ]
      }
    ]
  }
];

export const cityCoords = {
  'Delhi': { lat: 28.6139, lon: 77.2090 },
  'Mumbai': { lat: 19.0760, lon: 72.8777 },
  'Palava': { lat: 19.2000, lon: 73.0000 },
  'Chennai': { lat: 13.0827, lon: 80.2707 },
  'Ahmedabad': { lat: 23.0225, lon: 72.5714 },
  'Pune': { lat: 18.5204, lon: 73.8567 },
  'Hyderabad': { lat: 17.3850, lon: 78.4867 },
  'Kolkata': { lat: 22.5726, lon: 88.3639 },
  'Bangalore': { lat: 12.9716, lon: 77.5946 }
};

export const cityDataAvailable = {
  'Delhi': true,
  'Mumbai': false,
  'Palava': false,
  'Chennai': false,
  'Ahmedabad': false,
  'Pune': false,
  'Hyderabad': false,
  'Kolkata': false,
  'Bangalore': false
};

// Define which indicators have data available (only for Delhi)
export const indicatorsWithData = {
  'Delhi': [
    'Power outages (duration and geographic scope)',
    'Power Outage',
    'Groundwater stress levels',
    'No show rate among informal workers',
    'Daily wage loss (street vendors, rickshaw pullers)',
    'Lost labor hours due to heat stress',
    '% of workers without access to shade/water',
    'Reported workplace heat illness cases'
  ]
};

// Sector priority weights
export const sectorPriorityWeights = {
  'Built Environment & Public Health': 10,
  'Social Welfare & Humanitarian Response': 10,
  'Daily Wage Labor': 8,
  'Urban Informal services': 8,
  'Construction': 7,
  'Manufacturing and industrial': 7,
  'Water': 9,
  'Power': 8,
  'Buildings (Public)': 7,
  'Education': 6,
  'Buildings (Educational)': 6,
  'Transport': 5,
  'Urban Planning & Meteorology': 3,
  'Green Infrastructure & Geospatial Analysis': 3
};

export const cities = [
  { name: 'Delhi', image: 'city/delhi.jpg' },
  { name: 'Mumbai', image: 'city/mumbai.jpg' },
  { name: 'Palava', image: 'city/palava.jpg' },
  { name: 'Chennai', image: 'city/chennai.jpg' },
  { name: 'Ahmedabad', image: 'city/ahmedabad.jpg' },
  { name: 'Pune', image: 'city/pune.jpg' },
  { name: 'Hyderabad', image: 'city/hyderabad.jpg' },
  { name: 'Kolkata', image: 'city/kolkata.jpg' },
  { name: 'Bangalore', image: 'city/bangalore.jpg' }
];

